import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { getApiBaseUrl } from '@/config/env';
import { STORAGE_KEYS } from '@/constants/storage';
import type { ApiResponse } from '@/types/api';
import type { LoginResponse } from '@/types/auth';
import { ApiError } from '@/utils/apiError';
import {
  dispatchModuleAccessDenied,
  dispatchPermissionDenied,
  isModuleNotEnabledMessage,
  parseModuleFromAccessMessage,
} from '@/utils/moduleNavigation';
import { resolveModuleSignInPath } from '@/utils/authRedirect';
import {
  clearEntryModuleStorage,
  readStoredEntryModule,
  readStoredWorkspaceModule,
} from '@/utils/moduleScope';
import { isOpenAccessRoute } from '@/utils/openAccessRoutes';
import { shouldSkipColdSessionBootstrap } from '@/utils/sessionBootstrap';
import { isSigningOut } from '@/utils/authTransition';
import { purgeLegacyAuthFromBrowser } from '@/core/security/purgeLegacyAuthStorage';
import { clearWorkspaceScopeStorage } from '@/utils/workspaceScope';
import { getActiveLanguageCode } from '@/i18n/languageContext';

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
  withCredentials: true,
});

let isRefreshing = false;
let isRedirectingToSignIn = false;
let refreshQueue: Array<{
  resolve: () => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(error: unknown) {
  refreshQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve();
  });
  refreshQueue = [];
}

function isAuthScopeRequest(url?: string): boolean {
  if (!url) return false;
  return url.includes('/v1/auth/');
}

/** Auth endpoints that must not trigger token refresh / forced logout on 401. */
function isPublicAuthEndpoint(url?: string): boolean {
  if (!url) return false;
  return (
    url.includes('/v1/auth/login') ||
    url.includes('/v1/auth/refresh') ||
    url.includes('/v1/auth/session') ||
    url.includes('/v1/auth/logout') ||
    url.includes('/v1/auth/register') ||
    url.includes('/v1/auth/forgot-password') ||
    url.includes('/v1/auth/reset-password') ||
    url.includes('/v1/auth/sso/')
  );
}

function isPublicAppRoute(pathname?: string): boolean {
  if (!pathname) return false;
  return shouldSkipColdSessionBootstrap(pathname);
}

function shouldAttachWorkspaceHeaders(url?: string): boolean {
  if (!url || isAuthScopeRequest(url)) {
    return false;
  }
  // Only attach after a workspace scope was chosen for the current browser session.
  return sessionStorage.getItem('fattahone_workspace_scope_ready') === 'true';
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // FormData must not use the default application/json header — browser sets multipart boundary.
  if (config.data instanceof FormData && config.headers) {
    if (typeof config.headers.setContentType === 'function') {
      config.headers.setContentType(false);
    } else {
      delete config.headers['Content-Type'];
    }
  }

  if (config.headers) {
    const languageCode = getActiveLanguageCode().replace('_', '-');
    config.headers['Accept-Language'] = languageCode;
  }

  if (!shouldAttachWorkspaceHeaders(config.url)) {
    return config;
  }
  const companyRaw = localStorage.getItem(STORAGE_KEYS.SELECTED_COMPANY);
  if (companyRaw && config.headers) {
    try {
      const company = JSON.parse(companyRaw) as { id?: number };
      if (company.id) {
        config.headers['X-Company-Id'] = String(company.id);
      }
    } catch {
      /* ignore */
    }
  }
  const storeRaw = localStorage.getItem(STORAGE_KEYS.SELECTED_STORE);
  if (storeRaw && config.headers) {
    try {
      const store = JSON.parse(storeRaw) as { id?: number };
      if (store.id) {
        config.headers['X-Store-Id'] = String(store.id);
      }
    } catch {
      /* ignore */
    }
  }
  return config;
});

function redirectAfterSessionEnded() {
  if (isRedirectingToSignIn || isSigningOut() || isPublicAppRoute(window.location.pathname)) {
    return;
  }
  isRedirectingToSignIn = true;
  const loginUrl = resolveModuleSignInPath({
    pathname: window.location.pathname,
    entryModule: readStoredEntryModule(),
    activeModule: readStoredWorkspaceModule(),
  });
  clearAuthStorage();
  void axios.post(`${getApiBaseUrl()}/v1/auth/logout`, {}, { withCredentials: true }).catch(() => {
    /* the backend may already have cleared the inactive user's cookies */
  });
  window.location.replace(loginUrl);
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const isSessionBootstrap = original?.url?.includes('/v1/session/init');
    const status = error.response?.status;

    if (isSessionBootstrap && status === 401 && original && !original._retry) {
      original._retry = true;
      try {
        await axios.post<ApiResponse<LoginResponse>>(
          `${getApiBaseUrl()}/v1/auth/refresh`,
          {},
          { withCredentials: true },
        );
        return api(original);
      } catch (refreshError) {
        redirectAfterSessionEnded();
        return Promise.reject(ApiError.fromUnknown(refreshError));
      }
    }

    if (isSessionBootstrap && (status === 401 || status === 403 || status === 500)) {
      // Do not clear cookies here — init 401 often means "not logged in yet" and races with
      // in-flight password/SSO login; clearing would revoke cookies that were just issued.
      return Promise.reject(ApiError.fromUnknown(error));
    }

    if (status === 401 && original && !original._retry) {
      if (isSigningOut()) {
        return Promise.reject(ApiError.fromUnknown(error));
      }

      const onPublicAuthPage = isPublicAppRoute(window.location.pathname);
      if (isPublicAuthEndpoint(original.url) || onPublicAuthPage) {
        return Promise.reject(ApiError.fromUnknown(error));
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: () => resolve(api(original)),
            reject,
          });
        });
      }

      original._retry = true;
      isRefreshing = true;

      try {
        await axios.post<ApiResponse<LoginResponse>>(
          `${getApiBaseUrl()}/v1/auth/refresh`,
          {},
          { withCredentials: true },
        );
        processQueue(null);
        return api(original);
      } catch (refreshError) {
        processQueue(refreshError);
        if (!isSessionBootstrap) {
          redirectAfterSessionEnded();
        }
        return Promise.reject(ApiError.fromUnknown(refreshError));
      } finally {
        isRefreshing = false;
      }
    }

    const apiMessage =
      typeof error.response?.data?.message === 'string'
        ? error.response.data.message
        : typeof error.response?.data?.error === 'string'
          ? error.response.data.error
          : undefined;
    if (error.response?.status === 403 && isModuleNotEnabledMessage(apiMessage)) {
      dispatchModuleAccessDenied({
        message: apiMessage ?? 'Module is not enabled for this organization',
        module: apiMessage ? parseModuleFromAccessMessage(apiMessage) : null,
      });
    } else if (
      status === 403 &&
      original &&
      !isSessionBootstrap &&
      (original.method ?? 'GET').toUpperCase() === 'GET' &&
      !original.url?.includes('/v1/auth/') &&
      !isOpenAccessRoute(window.location.pathname)
    ) {
      const deniedMessage =
        apiMessage && !apiMessage.toLowerCase().includes('session scoped')
          ? apiMessage
          : 'You do not have permission to view this data.';
      dispatchPermissionDenied({ message: deniedMessage });
    }

    return Promise.reject(ApiError.fromUnknown(error));
  },
);

export function clearAuthStorage() {
  purgeLegacyAuthFromBrowser();
  clearEntryModuleStorage();
  clearWorkspaceScopeStorage();
}

/** Cookies carry tokens — no client-side token persistence. */
export function setAuthTokens(_accessToken: string, _refreshToken: string) {
  /* intentionally empty */
}

export async function unwrap<T>(promise: Promise<{ data: ApiResponse<T> }>): Promise<T> {
  try {
    const { data } = await promise;
    if (!data.success) {
      throw ApiError.fromResponse(data);
    }
    return data.data as T;
  } catch (err) {
    throw ApiError.fromUnknown(err);
  }
}

export default api;
